<script setup lang="ts">
/* eslint-disable no-redeclare */
import Swiper from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import type { CarInfo } from '@/interfaces/car-info-cassette/carInfo'

const nuxtApp = useNuxtApp()
const { carInfo, isModelGrade } = defineProps<{
  carInfo: CarInfo
  isModelGrade: boolean
}>()

// 新車価格
const newCarPrice: (string | number)[] = nuxtApp.$createDisplayCarPrice(
  carInfo.newcar_price_lower_limit,
  carInfo.newcar_price_upper_limit
)

// 中古車価格
const usedPriceLowerLimit = Number(carInfo.usedcar_min_price) * 10000
const usedPriceUpperLimit = Number(carInfo.usedcar_max_price) * 10000

const usedCarPrice = nuxtApp.$createDisplayCarPrice(
  usedPriceLowerLimit,
  usedPriceUpperLimit
)
const thumbSwiperActiveIndex = ref(0)

const initSwiper = () => {
  nextTick(() => {
    const thumbSwiper = new Swiper('.slider-thumbnail', {
      observer: true,
      slidesPerView: 3.5,
    })
    const swiper = new Swiper('.slider', {
      modules: [Pagination, Navigation],
      pagination: {
        el: '.swiper-pagination',
        type: 'fraction',
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      freeMode: true,
      slidesPerView: 'auto',
    })
    thumbSwiper.on('click', function () {
      swiper.slideTo(thumbSwiper.clickedIndex)
    })
    swiper.on('slideChange', function () {
      thumbSwiperActiveIndex.value = swiper.activeIndex
    })
  })
}
onMounted(() => {
  initSwiper()
})
</script>

<template>
  <div
    v-if="carInfo"
    class="contents"
    :class="[isModelGrade ? 'shadow-sm' : '']"
  >
    <div class="basic-content-tab">
      <div class="sales-status">
        <span v-if="carInfo.current_model_flg" class="tags">現行販売車</span>
        <span v-if="isModelGrade && carInfo.special_flg" class="tags"
          >特別・限定</span
        >
        <div v-if="isModelGrade" class="sales-period">
          {{ carInfo.sales_start_date }}
          <span> 発売モデル</span>
        </div>
        <div
          v-else-if="carInfo.current_model_flg && carInfo.sales_start_date"
          class="sales-period"
        >
          {{ carInfo.sales_start_date + '〜販売中' }}
        </div>
        <div
          v-else-if="carInfo.sales_start_date && carInfo.sales_end_date"
          class="sales-period"
        >
          {{ carInfo.sales_start_date + '〜' + carInfo.sales_end_date }}
        </div>
      </div>
      <div class="title">
        <span>{{ carInfo.brand_name + ' ' }} </span>
        <span>{{ carInfo.car_name + ' ' }} </span>
        <span>{{ carInfo.model_name }}</span>
      </div>
    </div>
    <div class="swiper">
      <div class="swiper-button-prev" />
      <div class="swiper-button-next" />
      <div class="slider">
        <div class="swiper-pagination shadow-sm" />
        <div class="swiper-wrapper">
          <div v-for="item in carInfo.images" :key="item" class="swiper-slide">
            <div class="slide">
              <img
                class="slide-image"
                :src="item"
                onerror="
                  this.src =
                    'https://img.goo-net.com/goo/usedcar/nophoto_big.jpg'
                "
                loading="eager"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isModelGrade" class="sales-start">
      <span>{{ carInfo.recent_debut_date_jpn }}</span>
      <span> 発売モデル</span>
    </div>
    <div class="swiper">
      <div class="slider-thumbnail">
        <div class="swiper-wrapper thumbnail">
          <div
            v-for="(item, index) in carInfo.images"
            :key="item"
            :class="[
              thumbSwiperActiveIndex === index
                ? 'swiper-slide-thumb-active'
                : '',
            ]"
            class="swiper-slide"
          >
            <div class="thumbnail-slide">
              <img
                class="thumbnail-slide-image"
                :src="item"
                onerror="
                  this.src =
                    'https://img.goo-net.com/goo/usedcar/nophoto_big.jpg'
                "
                loading="eager"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="attention">
      ※画像は他グレードと共通のものを使用しております。
    </div>
    <div class="container contents">
      <div class="row">
        <div
          class="new-car-price-title"
          :class="[isModelGrade ? 'model-grade' : 'model-top']"
        >
          <div class="price-title title_left">
            <span>新車価格</span>
          </div>
          <div class="price-caset">
            <div
              v-if="newCarPrice.length === 2"
              :class="[isModelGrade ? 'model-grade' : 'model-top']"
              class="new-car-price"
            >
              <div class="new-car-integer">
                {{ Number(newCarPrice[0]).toLocaleString() }}〜<br />
              </div>
              <div class="new-car-integer">
                {{ Number(newCarPrice[1]).toLocaleString()
                }}<span class="currency">円</span>
              </div>
            </div>
            <div
              v-else-if="newCarPrice[0] === 'ーー'"
              :class="[isModelGrade ? 'model-grade' : 'model-top']"
              class="new-car-price"
            >
              <span class="new-car-integer single">{{ newCarPrice[0] }}</span>
              <span class="currency">円</span>
            </div>
            <div
              v-else
              :class="[isModelGrade ? 'model-grade' : 'model-top']"
              class="new-car-price"
            >
              <span class="new-car-integer single">{{
                Number(newCarPrice[0]).toLocaleString()
              }}</span>
              <span class="currency">円</span>
            </div>
          </div>
        </div>
        <div
          class="old-car-price-title"
          :class="[isModelGrade ? 'model-grade' : 'model-top']"
        >
          <div class="price-title title_right">
            <span>中古車価格</span>
          </div>
          <div class="price-caset">
            <div
              v-if="usedCarPrice.length === 2"
              :class="[isModelGrade ? 'model-grade' : 'model-top']"
              class="old-car-price"
            >
              <span class="old-car-integer">{{
                Number(usedCarPrice[0]).toLocaleString()
              }}</span>
              <span class="old-car-integer"
                ><span class="wavy">〜</span><br />{{
                  Number(usedCarPrice[1]).toLocaleString()
                }}</span
              >
              <span class="currency">円</span>
            </div>
            <div
              v-else-if="usedCarPrice[0] === 'ーー'"
              :class="[isModelGrade ? 'model-grade' : 'model-top']"
              class="old-car-price"
            >
              <span class="old-car-integer single">{{ usedCarPrice[0] }}</span>
              <span class="currency">円</span>
            </div>
            <div
              v-else
              :class="[isModelGrade ? 'model-grade' : 'model-top']"
              class="old-car-price"
            >
              <span class="old-car-integer single">{{
                Number(usedCarPrice[0]).toLocaleString()
              }}</span>
              <span class="currency">円</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.contents {
  padding-top: 18px;
  background-color: #ffffff;
  padding: 0;
  padding-top: 15px;
}
.basic-content-tab {
  padding-left: 8px;
}
.sales-status {
  display: flex;
  align-items: center;
}
.sales-period {
  font-size: 12px;
}
.sales-start {
  font-size: 12px;
  padding-left: 8px;
  padding-bottom: 16px;
}
.attention {
  font-size: 12px;
  padding-left: 8px;
  padding-top: 10px;
}
.tags {
  background-color: #000;
  color: #fff !important;
  border-radius: 2px;
  font-size: 11px;
  padding: 4px 6px;
  margin-right: 6px;
}
.price-title {
  min-height: 25px;
  font-size: 11px;
  vertical-align: middle;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.slider,
.slider-thumbnail {
  overflow: hidden;
}
.slider {
  border-top: 1px solid #0000001a;
}
.swiper-pagination {
  display: flex;
  justify-content: center;
  font-family: 'Roboto-Regular';
  text-align: center;
  align-items: center;
  width: 65px;
  top: 7px;
  margin-left: 80%;
  height: 23px;
  border-radius: 4px;
  background: #000000;
  color: #ffffff;
}
.swiper-button-disabled {
  display: none;
}
.swiper-button-prev {
  left: 0px;
  width: 24px;
  height: 44px;
  background: #000000;
  border: 1px solid #fff;
  border-radius: 10%;
  opacity: 10%;
}
.swiper-button-next {
  right: 0px;
  width: 24px;
  height: 44px;
  background: #000000;
  border: 1px solid #fff;
  border-radius: 10%;
  opacity: 10%;
}
.swiper-button-next::after {
  color: #fff;
  font-size: 12px;
}
.swiper-button-prev::after {
  color: #fff;
  font-size: 12px;
}
.swiper-wrapper.thumbnail {
  height: 59px;
}

.slide-image {
  width: 359px;
  height: 179.5px;
  display: flex;
  margin: 8px auto 8.5px auto;
  vertical-align: middle;
  object-fit: cover;
}
.thumbnail-slide {
  height: 59px;
  display: flex;
  align-items: center;
}
.thumbnail-slide-image {
  border-radius: 4px;
  width: 96px;
  height: 48px;
  display: flex;
  margin: 0 auto;
  vertical-align: middle;
  object-fit: cover;
}

.swiper-slide-thumb-active .thumbnail-slide-image {
  border: 2px solid #df2020;
  width: 99px;
  height: 51px;
  border-radius: 4px;
  margin: 0 auto;
}
.row {
  display: flex;
  margin: 0;
}
.title {
  font-size: 15px;
  font-weight: bold;
  padding-top: 10px;
  padding-bottom: 8px;
}
.model-top.new-car-price-title {
  margin-left: 16px;
  padding: 0px;
  width: 45%;
}
.model-top.old-car-price-title {
  margin-left: 8px;
  padding: 0px;
  width: 45%;
}
.price-caset {
  margin-top: 4px;
}
.model-top.new-car-price {
  font-family: 'Roboto-Bold';
  height: auto;
  width: fit-content;
  margin: 0 auto;
}
.model-top.old-car-price {
  text-align: center;
  font-family: 'Roboto-Bold';
  height: auto;
  width: fit-content;
  margin: 0 auto;
}
.model-top .new-car-integer {
  font-size: 18px;
}
.model-top .new-car-integer.single {
  font-size: 24px;
}
.model-top.old-car-price .wavy {
  font-size: 18px;
}
.model-top .old-car-integer {
  font-size: 18px;
}
.model-top .old-car-integer.single {
  font-size: 24px;
}
.model-grade.new-car-price-title {
  margin-left: 16px;
  margin-bottom: 12px;
  padding: 0px;
  width: 45%;
}
.model-grade.old-car-price-title {
  margin-left: 16px;
  margin-bottom: 12px;
  padding: 0px;
  width: 45%;
}
.model-grade.new-car-price {
  font-family: 'Roboto-Bold';
  height: auto;
  width: fit-content;
  margin: 0 auto;
}
.model-grade.old-car-price {
  text-align: center;
  font-family: 'Roboto-Bold';
  height: auto;
  width: fit-content;
  margin: 0 auto;
}
.model-grade .new-car-integer {
  font-size: 24px;
}
.model-grade .old-car-integer {
  font-size: 16px;
}
.model-grade .old-car-integer.single {
  font-size: 24px;
}
.currency {
  font-family:
    Hiragino Kaku Gothic Pro-W6,
    Helvetica,
    sans-serif;
  font-size: 12px;
  font-weight: bold;
}
.title_left {
  width: 100%;
  color: #000000;
  background-color: #f4f6fa;
}
.title_right {
  width: 100%;
  color: #ffffff;
  background-color: #000000;
}
.c-price {
  padding: 0;
  font-weight: bold;
}
</style>
